
import { Movie } from "@/types/movie";

/**
 * Section header text generation with person information and icons
 */
export const getGroupHeaderText = (sectionType: string, results: Movie[]) => {
  if (results.length === 0) return 'No results';
  
  const firstResult = results[0];
  const personName = firstResult?.searchMeta?.personName;
  
  switch (sectionType) {
    case 'director':
      return personName 
        ? `🎬 Movies directed by ${personName}`
        : '🎬 Director matches';
    case 'actor':
      return personName 
        ? `✨ Movies starring ${personName}`
        : '✨ Actor matches';
    case 'title':
      return '🍿 Title matches';
    default:
      return 'Other results';
  }
};

/**
 * Enhanced sorting by popularity with quality boost
 */
export const sortByPopularity = (a: Movie, b: Movie) => {
  // Apply quality boost to sorting
  const aQualityBoost = a.searchMeta?.matchQuality === 'high' ? 10 : 
                        a.searchMeta?.matchQuality === 'medium' ? 5 : 0;
  const bQualityBoost = b.searchMeta?.matchQuality === 'high' ? 10 : 
                        b.searchMeta?.matchQuality === 'medium' ? 5 : 0;
  
  const aScore = (a.popularity || a.searchMeta?.popularityScore || 0) + aQualityBoost;
  const bScore = (b.popularity || b.searchMeta?.popularityScore || 0) + bQualityBoost;
  
  return bScore - aScore;
};

/**
 * Group results by source with comprehensive metadata and proper ordering
 */
export const groupResultsBySource = (results: Movie[]) => {
  const directorResults = results.filter(r => r.searchMeta?.source === "director");
  const actorResults = results.filter(r => r.searchMeta?.source === "actor"); 
  const titleResults = results.filter(r => 
    !r.searchMeta?.source || 
    r.searchMeta?.source === "title" || 
    r.searchMeta?.source === "movie"
  );
  
  // Sort each section with enhanced sorting
  directorResults.sort(sortByPopularity);
  actorResults.sort(sortByPopularity);
  titleResults.sort(sortByPopularity);
  
  const sections = [];
  
  // Add sections in the order they should appear (server already ordered them)
  if (directorResults.length > 0) {
    sections.push({ 
      type: 'director', 
      results: directorResults,
      personName: directorResults[0]?.searchMeta?.personName,
      isProgressiveMatch: directorResults[0]?.searchMeta?.isProgressiveMatch
    });
  }
  if (actorResults.length > 0) {
    sections.push({ 
      type: 'actor', 
      results: actorResults,
      personName: actorResults[0]?.searchMeta?.personName,
      isProgressiveMatch: actorResults[0]?.searchMeta?.isProgressiveMatch
    });
  }
  if (titleResults.length > 0) {
    sections.push({ 
      type: 'title', 
      results: titleResults,
      isProgressiveMatch: titleResults[0]?.searchMeta?.isProgressiveMatch
    });
  }
  
  return sections;
};
